import { QueryClient } from '@tanstack/react-query'
import { isSessionLive } from 'api/sessionLiveness'

/** One retry beyond the initial attempt — enough for a blip, not a stampede. */
const MAX_QUERY_RETRIES = 1

/**
 * `buildAppError` (frontend-utils) rejects with a plain object carrying `status`,
 * so match on the shape rather than an Error subclass.
 */
function isAuthError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) return false
  const status: unknown = error.status
  return status === 401 || status === 403
}

/**
 * Without `defaultOptions`, React Query v5 defaults to `retry: 3`. Combined with the
 * fetcher's own 401-refresh retry and the ~117 Orval hooks mounted across the
 * dashboard/visit screens, a single token expiry fanned out into a burst of refresh
 * attempts.
 *
 * An auth error still has to be retried, though, and the reason is subtle. The legacy
 * refresh path is single-flighted by `legacyRefreshInFlight` (`initOrvalTokenModules.ts`),
 * which hands every concurrent loser `false` — "not refreshed" — so the frontend-utils
 * fetcher skips its `continue` and throws the original error instead of replaying the
 * request. Those losers are not broken sessions; they are queries that arrived a few
 * milliseconds after the winner and are one retry away from succeeding with the rotated
 * token (`makeRequest` re-reads it via `getAuthTokenModule()` on every attempt). Refusing
 * to retry them strands a fan-out of dashboard and visit-popup queries on a token that is
 * already valid — and because callers like `useVisitLists` read only `{ data, isLoading }`,
 * that surfaces as an EMPTY waiting room rather than an error.
 *
 * "One retry away" is a race, not a guarantee: nothing couples the retry to the winner's
 * `REFRESH_TOKEN_SUCCESS`. `retryDelay` is deliberately NOT overridden here so React
 * Query's default exponential backoff (~1s before the first retry) gives the refresh POST
 * its head start; a refresh slower than that loses, and the loser spends its one retry on
 * the pre-rotation token. See `initOrvalTokenModules.ts` for why the obvious shared-promise
 * fix deadlocks and must not be attempted.
 *
 * `isAuthError` matches 401 AND 403 on purpose — it mirrors frontend-utils' `isUnauthorized`,
 * which is what decides whether the fetcher attempts a refresh at all. Both statuses can
 * therefore produce a race loser that needs this retry, so narrowing this to 401 would
 * reintroduce the empty-waiting-room stranding for the 403 half.
 *
 * Gated on the session still being live so this cannot become the stampede it replaced:
 * once a refresh has genuinely failed and LOGOUT has landed, retrying would send every
 * mounted hook back through `refreshTokens` for another doomed refresh and another LOGOUT
 * dispatch. That flag is read through `sessionLiveness` rather than `app/store` because a
 * dozen reducers import `client` from here — see that module for the cycle.
 *
 * This lives in its own module rather than beside `ReactQueryProvider` so that file exports
 * only its component: `react-refresh/only-export-components` flags a non-component export
 * alongside one, and a store/reducer importing the client should not have to pull a `.tsx`
 * component module into its graph.
 */
export const client = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount >= MAX_QUERY_RETRIES) return false
        if (!isAuthError(error)) return true
        return isSessionLive()
      },
    },
  },
})
